// The one per-vertex attribute of the engine, on WebGPU (R553).
//
// `gl/corner.ts` states why the corner is a real buffer and not
// `gl_VertexID`: Firefox on a Mac emulates a draw whose attribute 0 is not a
// per-vertex array. WebGPU has `@builtin(vertex_index)` and no such
// emulation, but the corner stays a buffer all the same, because the vertex
// shader is ONE source for both backends and it reads `aCorner` at location
// 0 in each of them.
//
// The buffer is one per context, and it registers before any batch, so the
// restore rebuilds it before a draw asks for it: `context.ts` recreates its
// resources in registration order.

import { CORNER_DATA } from '../gl/corner';
import { BUFFER_COPY_DST, BUFFER_VERTEX } from './flags';
import type { Gpu, GpuResource } from './context';

/** The static corner buffer of one context. `corners` hands it out. */
export class GpuCornerBuffer implements GpuResource {
  private vbo: GPUBuffer | null = null;

  /** The live handle. It is a NEW one after every device restore. */
  get buffer(): GPUBuffer | null { return this.vbo; }

  create(device: GPUDevice): void {
    this.destroy();
    const vbo = device.createBuffer({
      label: 'fast: corner',
      size: CORNER_DATA.byteLength,
      usage: BUFFER_VERTEX | BUFFER_COPY_DST,
    });
    device.queue.writeBuffer(vbo, 0, CORNER_DATA);
    this.vbo = vbo;
  }

  destroy(): void {
    if (this.vbo) this.vbo.destroy();
    this.vbo = null;
  }
}

const BUFFERS = new WeakMap<Gpu, GpuCornerBuffer>();

/**
 * The one corner buffer of a context. The first caller registers it, so it
 * stands in front of every batch and every pass in the restore order.
 */
export function corners(gpu: Gpu): GpuCornerBuffer {
  const held = BUFFERS.get(gpu);
  if (held) return held;
  const made = new GpuCornerBuffer();
  BUFFERS.set(gpu, made);
  gpu.register(made);
  return made;
}
